import { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, RouterProvider, Routes, createBrowserRouter } from 'react-router';
import { Skeleton, ToastProvider } from '@awj/ui';

import { AdminLayout } from './components/AdminLayout.tsx';
import { SignIn } from './components/SignIn.tsx';
import { I18nProvider, useT } from './i18n/index.tsx';
import { SessionProvider, useSession } from './lib/session.tsx';
import { CategoriesPage } from './pages/CategoriesPage.tsx';
import { CategoryEditorPage } from './pages/CategoryEditorPage.tsx';
import { CouponEditorPage } from './pages/CouponEditorPage.tsx';
import { CouponsPage } from './pages/CouponsPage.tsx';
import { ReviewReplyPage } from './pages/ReviewReplyPage.tsx';
import { StaffInvitePage } from './pages/StaffInvitePage.tsx';
import { CustomerDetailPage } from './pages/CustomerDetailPage.tsx';
import { CustomersPage } from './pages/CustomersPage.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { InquiriesPage } from './pages/InquiriesPage.tsx';
import { NotFoundPage } from './pages/NotFoundPage.tsx';
import { OrderDetailPage } from './pages/OrderDetailPage.tsx';
import { OrdersPage } from './pages/OrdersPage.tsx';
import { ProductEditorPage } from './pages/ProductEditorPage.tsx';
import { ProductsPage } from './pages/ProductsPage.tsx';
import { ReviewsPage } from './pages/ReviewsPage.tsx';
import { SettingsPage } from './pages/SettingsPage.tsx';

/*
 * The content studio is the only section that needs a rich-text editor, and the editor is the
 * largest thing the panel depends on. Every screen of it is split out so that weight lands on
 * the section that uses it rather than on every sign-in.
 */
const ContentPage = lazy(() =>
  import('./pages/ContentPage.tsx').then((module) => ({ default: module.ContentPage })),
);
const ContentPageEditorPage = lazy(() =>
  import('./pages/ContentPageEditorPage.tsx').then((module) => ({ default: module.ContentPageEditorPage })),
);
const AnnouncementEditorPage = lazy(() =>
  import('./pages/AnnouncementEditorPage.tsx').then((module) => ({ default: module.AnnouncementEditorPage })),
);
const FaqEditorPage = lazy(() =>
  import('./pages/FaqEditorPage.tsx').then((module) => ({ default: module.FaqEditorPage })),
);

/**
 * Query defaults for a back office.
 *
 * `staleTime` is short and refetch-on-focus is on, because two members of staff work the same
 * orders at once and a stale list is how one of them ships something the other cancelled. That
 * is the opposite trade from a storefront, where a shopper is alone with their own page.
 *
 * Mutations never retry. A failed POST may well have been applied before the connection dropped,
 * and quietly sending it again is how one refund becomes two.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
    mutations: { retry: 0 },
  },
});

/**
 * One catch-all route around the whole panel. A data router rather than `<BrowserRouter>`, for one
 * reason: only a data router can hold a navigation back while an editor asks whether to throw its
 * changes away. The routes themselves stay as `<Routes>` inside {@link Gate}.
 */
const router = createBrowserRouter([{ path: '*', element: <Gate /> }]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <Toasts>
          <SessionProvider>
            <RouterProvider router={router} />
          </SessionProvider>
        </Toasts>
      </I18nProvider>
    </QueryClientProvider>
  );
}

function Toasts({ children }: { children: React.ReactNode }) {
  const t = useT();

  return (
    <ToastProvider
      regionLabel={t('a11y.notifications')}
      dismissLabel={t('a11y.dismissNotification')}
    >
      {children}
    </ToastProvider>
  );
}

/**
 * Signed in, and staff.
 *
 * Three states, kept distinct: not asked yet, not signed in, and signed in as someone without a
 * staff role. Collapsing the first into the second flashes a sign-in form at someone who is
 * already signed in, every single load.
 */
function Gate() {
  const t = useT();
  const { signedIn, isStaff } = useSession();

  if (signedIn === null) {
    return (
      <div className="admin-boot" role="status">
        <Skeleton height="18px" width="10rem" />
        <span className="sr-only">{t('auth.checking')}</span>
      </div>
    );
  }

  if (!signedIn) {
    return <SignIn />;
  }

  if (!isStaff) {
    // A customer who found the URL. Told plainly rather than shown an empty panel full of 403s.
    return <SignIn reason="notStaff" />;
  }

  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route index element={<DashboardPage />} />

        <Route path="/products" element={<ProductsPage />} />
        {/*
          `new` and an id share one editor, and one ROUTE — the editor reads `new` as "nothing to
          load". Two routes rendering the same page look equivalent but are not: moving between
          them swaps one element for another, so saving a new product tore the editor down and
          built it again, and a correction typed while the rebuilt form was still reading the
          record back was overwritten by it.
        */}
        <Route path="/products/:id" element={<ProductEditorPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/categories/new" element={<CategoryEditorPage />} />
        <Route path="/categories/:id" element={<CategoryEditorPage />} />

        <Route path="/orders" element={<OrdersPage />} />
        {/* Orders are addressed by their number, which is what staff read out to each other. */}
        <Route path="/orders/:number" element={<OrderDetailPage />} />

        <Route path="/coupons" element={<CouponsPage />} />
        <Route path="/coupons/new" element={<CouponEditorPage />} />
        <Route path="/coupons/:id" element={<CouponEditorPage />} />

        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />

        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/reviews/:id/reply" element={<ReviewReplyPage />} />
        <Route path="/inquiries" element={<InquiriesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/staff/invite" element={<StaffInvitePage />} />

        <Route
          path="/content"
          element={
            <Suspense fallback={<Skeleton height="420px" />}>
              <ContentPage />
            </Suspense>
          }
        />

        <Route
          path="/content/pages/:pageId"
          element={
            <Suspense fallback={<Skeleton height="420px" />}>
              <ContentPageEditorPage />
            </Suspense>
          }
        />

        <Route
          path="/content/faq/new"
          element={
            <Suspense fallback={<Skeleton height="420px" />}>
              <FaqEditorPage />
            </Suspense>
          }
        />
        <Route
          path="/content/faq/:id"
          element={
            <Suspense fallback={<Skeleton height="420px" />}>
              <FaqEditorPage />
            </Suspense>
          }
        />

        <Route
          path="/content/announcements/new"
          element={
            <Suspense fallback={<Skeleton height="420px" />}>
              <AnnouncementEditorPage />
            </Suspense>
          }
        />
        <Route
          path="/content/announcements/:id"
          element={
            <Suspense fallback={<Skeleton height="420px" />}>
              <AnnouncementEditorPage />
            </Suspense>
          }
        />

        <Route path="/dashboard" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
